import * as React from 'react';

import type {FilterObject} from 'nutshell-core/types';

import {Card} from 'shells/card/card';
import {colors} from 'shells/colors/colors';
import {RANKED_COLORS} from 'shells/maps/consts';
import {ItemCountTable} from 'shells/table/item-count-table';
import {SectionLabel} from 'shells/typography/section-label';
import {useResizeObserver} from 'shells/utils/hooks/use-resize-observer';

import {SpecialState} from '../summary/special-state';
import type {TopTenReport} from '../summary/use-get-top-ten-report';

import {IndustriesDonutChart} from './industries-donut-chart';

import styles from './industries.module.css';

type Props = {
    industriesReport: TopTenReport | null | undefined;
    isLoading: boolean;
    isError: boolean;
    getFilteredListUrlPath: (filter: FilterObject) => string;
};

export function IndustriesWrapper(props: Props) {
    const [containerWidth, setContainerWidth] = React.useState<number>(400);
    const containerRef = React.useRef(null);
    // Set containerWidth as soon as we have the ref
    React.useEffect(() => {
        if (containerRef.current) {
            // @ts-expect-error
            setContainerWidth(containerRef.current.getBoundingClientRect().width);
        }
    }, [containerRef]);

    // Observe any resizing of the window and adjust containerWidth accordingly
    const [width] = useResizeObserver(containerRef.current);
    React.useEffect(() => {
        if (typeof width === 'number') {
            setContainerWidth(width);
        }
    }, [width]);

    // Remove 48px from width to account for margin/gap
    const donutWidth = containerWidth < 800 ? containerWidth : (containerWidth - 96) * 0.25;
    const tableWidth = (containerWidth - 96) * 0.75;

    const industries = props.industriesReport
        ? props.industriesReport.map((bucket, index) => ({
              ...bucket,
              // @ts-expect-error
              fill: colors[RANKED_COLORS[index]],
          }))
        : [];

    const renderContent = () => {
        if (props.isLoading) {
            return <SpecialState state='loading' />;
        } else if (props.isError) {
            return <SpecialState state='error' />;
        } else {
            return (
                <>
                    <IndustriesDonutChart
                        industries={industries}
                        width={Math.min(250, donutWidth)}
                        getFilteredListUrlPath={props.getFilteredListUrlPath}
                    />
                    <div className={styles['table-container']}>
                        <ItemCountTable
                            title='Industry'
                            items={industries}
                            // @ts-expect-error
                            width={tableWidth}
                            countColumnTitle='Number of visitors'
                        />
                    </div>
                </>
            );
        }
    };

    return (
        <div ref={containerRef}>
            <SectionLabel noBottomBorder={true}>Top 10 Industries</SectionLabel>
            <Card hasBorder={true} fitHeight={true}>
                <div className={styles['industries-wrapper']}>{renderContent()}</div>
            </Card>
        </div>
    );
}
